import { createContext, useContext, useState, useCallback, useEffect } from 'react'
import type { ReactNode } from 'react'
import { supabase } from '../lib/supabase'

import en from '../locales/en/common.json'
import pt from '../locales/pt/common.json'
import es from '../locales/es/common.json'

export type Lang = 'en' | 'pt' | 'es'

type JsonObj = { [k: string]: string | JsonObj }
const resources: Record<Lang, JsonObj> = { en, pt, es }

function resolve(obj: JsonObj, key: string): string {
  const parts = key.split('.')
  let cur: string | JsonObj = obj
  for (const p of parts) {
    if (cur && typeof cur === 'object') cur = (cur as JsonObj)[p]
    else return key
  }
  return typeof cur === 'string' ? cur : key
}

export const SUPPORTED_LANGS: { value: Lang; label: string }[] = [
  { value: 'pt', label: 'Português' },
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
]

interface LangState {
  lang: Lang
  setLang: (l: Lang) => void
  applyFromMetadata: (l: Lang) => void
  t: (key: string, vars?: Record<string, string | number>) => string
}

const LangContext = createContext<LangState | undefined>(undefined)

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    try { return (localStorage.getItem('lang') as Lang) || 'pt' } catch { return 'pt' }
  })

  const setLang = useCallback((l: Lang) => {
    setLangState(l)
    try { localStorage.setItem('lang', l) } catch {}
    // Persist to user metadata so preference follows user across devices
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) supabase.auth.updateUser({ data: { lang: l } }).catch(() => {})
    })
  }, [])

  // Called by UserPrefsSync when user logs in — apply saved preference
  const applyFromMetadata = useCallback((l: Lang) => {
    setLangState(l)
    try { localStorage.setItem('lang', l) } catch {}
  }, [])

  // O index.html nasce com lang="pt" fixo. Sem isto, uma pagina em ingles ou espanhol
  // se declara portuguesa: leitor de tela pronuncia com a fonetica errada e buscador
  // indexa com o idioma errado, o que pesa na landing, que existe para ser achada.
  useEffect(() => { document.documentElement.lang = lang }, [lang])

  const t = useCallback((key: string, vars?: Record<string, string | number>): string => {
    let str = resolve(resources[lang], key)
    if (str === key) str = resolve(resources.pt, key) // pt fallback
    if (vars) {
      Object.entries(vars).forEach(([k, v]) => {
        str = str.replace(`{{${k}}}`, String(v))
      })
    }
    return str
  }, [lang])

  return (
    <LangContext.Provider value={{ lang, setLang, applyFromMetadata, t }}>
      {children}
    </LangContext.Provider>
  )
}

export function useLang() {
  const ctx = useContext(LangContext)
  if (!ctx) throw new Error('useLang must be used within LangProvider')
  return ctx
}
